import { useState } from "react";
import { AsyncCombobox } from "@/components/shared/async-combobox";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type { PtoEntry } from "@/hooks/use-pto-entries";
import { usePtoEntries } from "@/hooks/use-pto-entries";
import { formatDate } from "@/lib/format";

interface PtoEntrySelectProps {
  value: string | null;
  onChange: (entryId: string, entry: PtoEntry) => void;
  selectedLabel?: string;
  disabled?: boolean;
  className?: string;
}

function entryLabel(entry: PtoEntry): string {
  const start = formatDate(entry.startDate);
  return entry.startDate === entry.endDate ? start : `${start} – ${formatDate(entry.endDate)}`;
}

/**
 * Server-side-searchable single-select for PTO entries — the per-entity
 * wrapper pattern (mirrors WidgetSelect.tsx). Used by ActivitySuggestionForm's
 * optional "attach to entry" picker.
 */
export function PtoEntrySelect({
  value,
  onChange,
  selectedLabel,
  disabled,
  className,
}: PtoEntrySelectProps) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);

  const { data, isFetching } = usePtoEntries(
    { search: debouncedSearch, status: "", sort: "startDate", sortDir: "desc" },
    { cursor: null, direction: "next" },
  );
  const entries = data?.data ?? [];

  return (
    <AsyncCombobox
      value={value}
      onChange={onChange}
      options={entries}
      getOptionValue={(entry) => entry.id}
      getOptionText={entryLabel}
      getOptionLabel={(entry) => (
        <span className="flex w-full items-center justify-between gap-2">
          <span>{entryLabel(entry)}</span>
          <span className="text-xs text-muted-foreground capitalize">{entry.type}</span>
        </span>
      )}
      search={search}
      onSearchChange={setSearch}
      selectedLabel={selectedLabel}
      loading={isFetching}
      disabled={disabled}
      className={className}
      placeholder="Attach to an entry…"
      searchPlaceholder="Search entries by note…"
      emptyText="No PTO entries found."
    />
  );
}
